// PlatformRoleTemplates — Custom RBAC §6.6 + §3.5.
// super_admin only — maintain the platform-wide library of role templates
// that tenant_admins clone from in /dashboard/admin/roles/new.
//
// Surface:
//   - list with industry_tag filter chips + 「+ 建立範本」 button
//   - inline editor Drawer (建立 + 編輯共用)
//   - 軟刪除（is_active=0）— already-cloned tenant roles 不受影響

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Plus, RefreshCw, ServerCrash, ShieldCheck, X, Save, ArrowUp, ArrowDown, Power, PowerOff,
} from "lucide-react";
import PageHeader from "../../components/PageHeader.js";
import EmptyState from "../../components/EmptyState.js";
import Drawer from "../../components/Drawer.js";
import { Field } from "../../components/Field.js";
import { useConfirmDialog } from "../../components/ConfirmDialog.js";
import PermissionPicker from "../../components/PermissionPicker.js";
import {
  listRoleTemplates,
  getRoleTemplate,
  createRoleTemplate,
  updateRoleTemplate,
  deleteRoleTemplate,
  friendlyRolesError,
  type RoleTemplate,
} from "../../lib/rolesApi.js";

type ListState =
  | { status: "loading" }
  | { status: "ready"; rows: RoleTemplate[] }
  | { status: "error"; message: string };

type Editor =
  | { mode: "create" }
  | { mode: "edit"; row: RoleTemplate };

const INDUSTRY_LABELS: Record<string, string> = {
  general: "通用",
  residential: "住宅",
  commercial: "商辦",
};
const INDUSTRY_TAGS: ReadonlyArray<string> = ["general", "residential", "commercial"];

export default function PlatformRoleTemplates(): React.ReactElement {
  const [state, setState] = useState<ListState>({ status: "loading" });
  const [industryFilter, setIndustryFilter] = useState<string>(""); // "" = all
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editor, setEditor] = useState<Editor | null>(null);
  const dlg = useConfirmDialog();

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      // Backend only filters by industry_tag for list. We filter is_active
      // client-side via the include_inactive checkbox; spec keeps inactive
      // visible to super_admin for re-activation / audit.
      const r = await listRoleTemplates(industryFilter || undefined);
      const filtered = includeInactive ? r.items : r.items.filter((t) => t.is_active);
      setState({ status: "ready", rows: filtered });
    } catch (err) {
      setState({ status: "error", message: friendlyRolesError(err) });
    }
  }, [industryFilter, includeInactive]);

  useEffect(() => { void load(); }, [load]);

  async function handleDelete(row: RoleTemplate): Promise<void> {
    const ok = await dlg.confirm(
      `確認封存範本「${row.name}」？已經 clone 出去的 tenant 自訂角色不受影響；但新的 tenant 將不再看到此範本。可從列表的「啟用」按鈕還原。`,
      { destructive: true, okLabel: "封存" },
    );
    if (!ok) return;
    try {
      await deleteRoleTemplate(row.id);
      await load();
    } catch (err) {
      await dlg.alert(friendlyRolesError(err), { title: "封存失敗" });
    }
  }

  async function handleReactivate(row: RoleTemplate): Promise<void> {
    try {
      await updateRoleTemplate(row.id, { is_active: true });
      await load();
    } catch (err) {
      await dlg.alert(friendlyRolesError(err), { title: "啟用失敗" });
    }
  }

  async function handleReorder(row: RoleTemplate, delta: number): Promise<void> {
    try {
      await updateRoleTemplate(row.id, { display_order: row.display_order + delta });
      await load();
    } catch (err) {
      await dlg.alert(friendlyRolesError(err), { title: "排序更新失敗" });
    }
  }

  if (state.status === "error") {
    return (
      <>
        <PageHeader title="平台角色範本" description="super_admin 維護的 catalog，tenant_admin 在建立自訂角色時可選為起點" />
        <EmptyState
          icon={ServerCrash}
          title="無法載入範本列表"
          description={state.message}
          action={
            <button onClick={() => void load()} className="btn-secondary text-sm">
              <RefreshCw className="h-4 w-4" /> 重試
            </button>
          }
        />
      </>
    );
  }

  const rows = state.status === "ready" ? state.rows : [];

  return (
    <>
      <PageHeader
        title="平台角色範本"
        description="super_admin 維護的 catalog，tenant_admin 在「建立自訂角色」 wizard 內可選為起點。clone 後與範本完全解耦。"
        actions={
          <>
            <button onClick={() => void load()} className="btn-ghost text-sm" aria-label="重新載入">
              <RefreshCw className={`h-4 w-4 ${state.status === "loading" ? "animate-spin" : ""}`} />
            </button>
            <button onClick={() => setEditor({ mode: "create" })} className="btn-primary text-sm">
              <Plus className="h-4 w-4" /> 建立範本
            </button>
          </>
        }
      />

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setIndustryFilter("")}
          className={[
            "px-3 py-1.5 rounded-lg text-xs font-medium transition-colors",
            industryFilter === "" ? "bg-accent/15 text-accent" : "bg-bg-surface text-text-secondary hover:bg-bg-hover border border-border",
          ].join(" ")}
        >
          全部
        </button>
        {INDUSTRY_TAGS.map((tag) => (
          <button
            key={tag}
            type="button"
            onClick={() => setIndustryFilter(tag)}
            className={[
              "px-3 py-1.5 rounded-lg text-xs font-medium transition-colors",
              industryFilter === tag ? "bg-accent/15 text-accent" : "bg-bg-surface text-text-secondary hover:bg-bg-hover border border-border",
            ].join(" ")}
          >
            {INDUSTRY_LABELS[tag] ?? tag}
          </button>
        ))}
        <label className="ml-auto flex items-center gap-2 text-2xs text-text-secondary cursor-pointer">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="rounded border-border bg-bg-base text-accent focus:ring-accent focus:ring-offset-0"
          />
          顯示已封存
        </label>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          icon={ShieldCheck}
          title={state.status === "loading" ? "載入中…" : "尚未建立範本"}
          description={state.status === "loading" ? undefined : "建立第一個範本後，tenant_admin 可在 /dashboard/admin/roles/new 選為起點。"}
          action={state.status === "loading" ? undefined : (
            <button onClick={() => setEditor({ mode: "create" })} className="btn-primary text-sm">
              <Plus className="h-4 w-4" /> 建立第一個範本
            </button>
          )}
        />
      ) : (
        <div className="rounded-xl border border-border bg-bg-surface divide-y divide-border">
          {rows.map((row, idx) => (
            <TemplateRow
              key={row.id}
              row={row}
              isFirst={idx === 0}
              isLast={idx === rows.length - 1}
              onEdit={() => setEditor({ mode: "edit", row })}
              onDelete={() => void handleDelete(row)}
              onReactivate={() => void handleReactivate(row)}
              onMoveUp={() => void handleReorder(row, -10)}
              onMoveDown={() => void handleReorder(row, 10)}
            />
          ))}
        </div>
      )}

      <Drawer
        open={!!editor}
        onClose={() => setEditor(null)}
        title={editor?.mode === "edit" ? `編輯範本：${editor.row.name}` : "建立平台角色範本"}
        size="lg"
      >
        {editor && (
          <TemplateForm
            initial={editor.mode === "edit" ? editor.row : null}
            onCancel={() => setEditor(null)}
            onSaved={() => { setEditor(null); void load(); }}
          />
        )}
      </Drawer>
    </>
  );
}

function TemplateRow({ row, isFirst, isLast, onEdit, onDelete, onReactivate, onMoveUp, onMoveDown }: {
  row: RoleTemplate; isFirst: boolean; isLast: boolean;
  onEdit: () => void; onDelete: () => void; onReactivate: () => void;
  onMoveUp: () => void; onMoveDown: () => void;
}): React.ReactElement {
  return (
    <div className={["flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between", !row.is_active ? "opacity-60" : ""].join(" ")}>
      <div className="flex items-start gap-3 min-w-0 sm:items-center">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg flex-shrink-0 bg-accent-subtle text-accent">
          <ShieldCheck className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-text-primary truncate">
            {row.name}
            {!row.is_active && <span className="ml-2 text-2xs text-text-muted">（已封存）</span>}
          </div>
          {row.description && (
            <div className="text-2xs text-text-muted truncate">{row.description}</div>
          )}
          {/* Metadata line. flex-wrap + whitespace-nowrap on each chip so
              narrow viewports wrap chip-by-chip instead of squeezing a
              Chinese label into single-character columns. */}
          <div className="text-2xs text-text-muted mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
            <code className="font-mono whitespace-nowrap">{row.id}</code>
            <span aria-hidden="true">·</span>
            <span className="whitespace-nowrap">{INDUSTRY_LABELS[row.industry_tag] ?? row.industry_tag}</span>
            <span aria-hidden="true">·</span>
            <span className="whitespace-nowrap">{row.suggested_permissions.length} 個建議權限</span>
            <span aria-hidden="true">·</span>
            <span className="whitespace-nowrap">order {row.display_order}</span>
          </div>
        </div>
      </div>
      {/* Action buttons: align right on desktop; full row at the bottom on
          mobile (which is what the user sees when the row stacks). */}
      <div className="flex items-center gap-1 flex-shrink-0 self-end sm:self-auto">
        <button type="button" onClick={onMoveUp} disabled={isFirst} className="btn-ghost text-xs disabled:opacity-30" title="上移">
          <ArrowUp className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={onMoveDown} disabled={isLast} className="btn-ghost text-xs disabled:opacity-30" title="下移">
          <ArrowDown className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={onEdit} className="btn-ghost text-xs">編輯</button>
        {row.is_active ? (
          <button type="button" onClick={onDelete} className="btn-ghost text-xs text-danger" title="封存">
            <PowerOff className="h-3.5 w-3.5" />
          </button>
        ) : (
          <button type="button" onClick={onReactivate} className="btn-ghost text-xs text-success" title="重新啟用">
            <Power className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function TemplateForm({ initial, onCancel, onSaved }: {
  initial: RoleTemplate | null;
  onCancel: () => void;
  onSaved: () => void;
}): React.ReactElement {
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [industryTag, setIndustryTag] = useState(initial?.industry_tag ?? "general");
  const [displayOrder, setDisplayOrder] = useState<number>(initial?.display_order ?? 0);
  const [permissions, setPermissions] = useState<Set<string>>(
    () => new Set(initial?.suggested_permissions ?? []),
  );
  const [submitting, setSubmitting] = useState(false);
  const dlg = useConfirmDialog();

  // When editing, fetch the full row in case `suggested_permissions` was
  // truncated in the list response. Idempotent.
  useEffect(() => {
    if (!initial) return;
    let alive = true;
    (async () => {
      try {
        const full = await getRoleTemplate(initial.id);
        if (!alive) return;
        setPermissions(new Set(full.suggested_permissions));
      } catch { /* keep what we had */ }
    })();
    return () => { alive = false; };
  }, [initial]);

  const dirty = useMemo(() => {
    if (!initial) return true;
    if (name.trim() !== initial.name) return true;
    if ((description.trim() || null) !== (initial.description ?? null)) return true;
    if (industryTag !== initial.industry_tag) return true;
    if (displayOrder !== initial.display_order) return true;
    if (permissions.size !== initial.suggested_permissions.length) return true;
    for (const p of initial.suggested_permissions) if (!permissions.has(p)) return true;
    return false;
  }, [initial, name, description, industryTag, displayOrder, permissions]);

  async function handleSave(): Promise<void> {
    if (!name.trim()) {
      await dlg.alert("請輸入範本名稱");
      return;
    }
    setSubmitting(true);
    try {
      if (initial) {
        await updateRoleTemplate(initial.id, {
          name: name.trim(),
          description: description.trim() || null,
          industry_tag: industryTag,
          suggested_permissions: [...permissions],
          display_order: displayOrder,
        });
      } else {
        await createRoleTemplate({
          name: name.trim(),
          description: description.trim() || null,
          industry_tag: industryTag,
          suggested_permissions: [...permissions],
          display_order: displayOrder,
        });
      }
      onSaved();
    } catch (err) {
      await dlg.alert(friendlyRolesError(err), { title: initial ? "儲存失敗" : "建立失敗" });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4">
      <Field label="名稱" required>
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="例：現場主管"
          maxLength={120}
        />
      </Field>
      <Field label="說明">
        <textarea
          className="textarea"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="（選填）顯示在 wizard 內的範本卡片下方"
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="行業標籤">
          <select
            className="select"
            value={industryTag}
            onChange={(e) => setIndustryTag(e.target.value)}
          >
            {INDUSTRY_TAGS.map((tag) => (
              <option key={tag} value={tag}>{INDUSTRY_LABELS[tag] ?? tag}</option>
            ))}
          </select>
        </Field>
        <Field label="顯示順序">
          <input
            type="number"
            className="input"
            value={displayOrder}
            onChange={(e) => setDisplayOrder(Number.isFinite(+e.target.value) ? +e.target.value : 0)}
          />
        </Field>
      </div>

      <div>
        <div className="text-xs font-medium text-text-secondary mb-2">建議權限</div>
        <PermissionPicker value={permissions} onChange={setPermissions} />
      </div>

      <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
        <button type="button" onClick={onCancel} className="btn-secondary text-sm" disabled={submitting}>
          <X className="h-4 w-4" /> 取消
        </button>
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={submitting || !dirty || !name.trim()}
          className="btn-primary text-sm"
        >
          <Save className="h-4 w-4" /> {submitting ? "儲存中…" : initial ? "儲存變更" : "建立範本"}
        </button>
      </div>
    </div>
  );
}
